import React, { useEffect, useRef, useState, memo } from 'react';

// Lazy loads the video source only when the component becomes visible,
// and pauses playback when it's out of view to save bandwidth/CPU.
const LazyVideo = memo(({ src, className = '', poster, loop = true, muted = true, playsInline = true, height = 250 }) => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
        if (entry.isIntersecting) setShouldLoad(true);
      },
      { rootMargin: '100px 0px', threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isIntersecting && isReady) {
      // Try play when visible
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } else {
      // Pause when out of view
      try { video.pause(); } catch {}
    }
  }, [isIntersecting, isReady]);

  const handleLoadStart = () => {
    setLoadError(false);
  };

  const handleError = () => {
    setLoadError(true);
    console.error(`Failed to load video: ${src}`);
  };

  return (
    <div ref={containerRef} className={`w-full overflow-hidden ${className}`} style={{ height }}>
      {shouldLoad ? (
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          preload="metadata"
          loop={loop}
          muted={muted}
          playsInline={playsInline}
          poster={poster}
          onCanPlayThrough={() => setIsReady(true)}
          onLoadStart={handleLoadStart}
          onError={handleError}
        >
          <source src={src} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      ) : (
        <div className="w-full h-full bg-white/10 animate-pulse flex items-center justify-center">
          <div className="text-white/60 text-sm">Loading video...</div>
        </div>
      )}
      {loadError && (
        <div className="w-full h-full bg-red-100 flex items-center justify-center">
          <div className="text-red-600 text-sm">Failed to load video</div>
        </div>
      )}
    </div>
  );
});

export default LazyVideo;
